import { useMemo, useState } from 'react';
import type { Solicitacao, Movimentacao } from '../../../../models';
import {
  calcularFunilStatus,
  calcularVolumePorTipo,
  calcularTempoMedioConclusao,
  listarAtrasadas,
  calcularPendenciasComuns,
  filtrarPorPeriodo,
  type PeriodoFiltro,
} from './solicitacoesDashboard.utils';
import FunilStatusChart from './components/FunilStatusChart';
import VolumePorTipoChart from './components/VolumePorTipoChart';
import TempoMedioConclusaoChart from './components/TempoMedioConclusaoChart';
import AtrasadasAgoraCard from './components/AtrasadasAgoraCard';
import PendenciasComunsChart from './components/PendenciasComunsChart';
import PeriodoSelector from './components/PeriodoSelector';

const SEM_FILTRO_DE_PERIODO: PeriodoFiltro = { de: null, ate: null };

interface SolicitacoesDashboardProps {
  /** Sempre a fila inteira (todas as Solicitações, não filtrada por status/tipo/mês) —
   * métricas de painel são sobre o todo, diferente da lista de trabalho do dia a dia. */
  itens: Solicitacao[];
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>;
  onSelect: (id: string) => void;
}

/**
 * Painel de métricas do módulo SI — só a fila inteira (sem os filtros de status/tipo/mês
 * da aba "Controle de Solicitações", que são pra triagem do dia a dia, não pra visão
 * consolidada). Primeiro recorte de métricas (mais podem entrar depois): funil por
 * status, volume por tipo, tempo médio de conclusão, urgentes agora e pendências mais
 * comuns — todas calculadas a partir dos mesmos dados que a fila já busca, sem query nova.
 */
export default function SolicitacoesDashboard({ itens, movimentacoesPorSolicitacao, onSelect }: SolicitacoesDashboardProps) {
  // "Tudo" (sem filtro) por padrão — mesmo recorte de sempre até a pessoa escolher um
  // período específico (ver PeriodoSelector). Filtra pela dataAbertura da Solicitação.
  const [periodo, setPeriodo] = useState<PeriodoFiltro>(SEM_FILTRO_DE_PERIODO);
  const itensDoPeriodo = useMemo(() => filtrarPorPeriodo(itens, periodo), [itens, periodo]);

  const funilStatus = useMemo(() => calcularFunilStatus(itensDoPeriodo), [itensDoPeriodo]);
  const volumePorTipo = useMemo(() => calcularVolumePorTipo(itensDoPeriodo), [itensDoPeriodo]);
  const tempoMedioConclusao = useMemo(() => calcularTempoMedioConclusao(itensDoPeriodo), [itensDoPeriodo]);
  const atrasadas = useMemo(
    () => listarAtrasadas(itensDoPeriodo, movimentacoesPorSolicitacao),
    [itensDoPeriodo, movimentacoesPorSolicitacao]
  );
  const pendenciasComuns = useMemo(
    () => calcularPendenciasComuns(itensDoPeriodo, movimentacoesPorSolicitacao),
    [itensDoPeriodo, movimentacoesPorSolicitacao]
  );

  return (
    <div className="space-y-4">
      <PeriodoSelector periodo={periodo} onChange={setPeriodo} />
      <div className="grid gap-4 md:grid-cols-2">
        <FunilStatusChart dados={funilStatus} />
        <VolumePorTipoChart dados={volumePorTipo} />
        <TempoMedioConclusaoChart dados={tempoMedioConclusao} />
        <AtrasadasAgoraCard itens={atrasadas} onSelect={onSelect} />
        <div className="md:col-span-2">
          <PendenciasComunsChart dados={pendenciasComuns} />
        </div>
      </div>
    </div>
  );
}
